import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Moon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OfferShift } from "@/pages/admin/OfferShift";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg } from "@/lib/api";
import { buildRange, fmtDay, fmtTime, isOvernight } from "@/lib/dates";

const OPEN = "__open__";
const NONE = "__none__";

const initial = (shift, preset, types) => {
  if (shift) {
    return { staff_id: shift.staff_id || OPEN, shift_type_id: shift.shift_type_id || NONE, location_id: shift.location_id || NONE,
      date: shift.start.slice(0, 10), start_time: fmtTime(shift.start), end_time: fmtTime(shift.end), notes: shift.notes || "",
      next_day: isOvernight(shift) };
  }
  const t = types[0];
  return { staff_id: preset?.staff_id || OPEN, shift_type_id: t?.id || NONE, location_id: NONE, date: preset?.date,
    start_time: t?.start_time || "09:00", end_time: t?.end_time || "17:00", notes: "",
    next_day: !!t && (t.ends_next_day || t.end_time <= t.start_time) };
};

export const ShiftDialog = ({ open, onOpenChange, shift, preset, staff, types, locations, onSaved }) => {
  const [f, setF] = useState(() => initial(shift, preset, types));
  useEffect(() => {
    if (open) setF(initial(shift, preset, types));
  }, [open, shift, preset, types]);

  const pickType = (id) => {
    const t = types.find((x) => x.id === id);
    setF({ ...f, shift_type_id: id, ...(t ? { start_time: t.start_time, end_time: t.end_time, next_day: t.ends_next_day || t.end_time <= t.start_time } : {}) });
  };
  const type = types.find((x) => x.id === f.shift_type_id);
  const forced = f.end_time <= f.start_time;
  const range = f.date ? buildRange(f.date, f.start_time, f.end_time, f.next_day) : null;

  const save = async (e) => {
    e.preventDefault();
    const body = { staff_id: f.staff_id === OPEN ? null : f.staff_id, shift_type_id: f.shift_type_id === NONE ? null : f.shift_type_id,
      location_id: f.location_id === NONE ? null : f.location_id, notes: f.notes, ...range };
    try {
      if (shift) await api.put(`/biz/shifts/${shift.id}`, body);
      else await api.post("/biz/shifts", body);
      toast.success("Shift saved");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/biz/shifts/${shift.id}`);
      toast.success("Shift deleted");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="shift-dialog">
        <DialogHeader><DialogTitle>{shift ? "Edit shift" : "Add shift"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Assigned to</Label>
            <Select value={f.staff_id} onValueChange={(v) => setF({ ...f, staff_id: v })}>
              <SelectTrigger data-testid="shift-staff-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={OPEN}>Open shift (anyone can claim)</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Shift type</Label>
            <Select value={f.shift_type_id} onValueChange={pickType}>
              <SelectTrigger data-testid="shift-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Custom</SelectItem>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.start_time}–{t.end_time})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Start date</Label><DatePicker value={f.date} onChange={(d) => setF({ ...f, date: d })} testId="shift-date-picker" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start</Label><Input type="time" required value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="shift-start-input" /></div>
            <div className="space-y-1.5"><Label>End</Label><Input type="time" required value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} data-testid="shift-end-input" /></div>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
            Ends the next day
            <Switch checked={forced || f.next_day} disabled={forced} onCheckedChange={(v) => setF({ ...f, next_day: v })} data-testid="shift-next-day-switch" />
          </label>
          {range && range.end.slice(0, 10) !== range.start.slice(0, 10) && (
            <p className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white" data-testid="shift-overnight-hint">
              Overnight: {fmtDay(range.start)} {f.start_time} → {fmtDay(range.end)} {f.end_time}
            </p>
          )}
          {type?.is_sleepover && (
            <p className="flex items-center gap-2 rounded-lg bg-purple-50 px-3 py-2 text-xs text-purple-800" data-testid="shift-sleepover-hint">
              <Moon className="h-4 w-4" /> Sleepover: £{type.sleepover_allowance.toFixed(2)} allowance, {type.unpaid_sleep_hours}h unpaid sleep
            </p>
          )}
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
              <SelectTrigger data-testid="shift-location-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No location</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="shift-notes-input" /></div>
          {shift && !shift.staff_id && new Date(shift.end) > new Date() && <OfferShift key={shift.id} shift={shift} />}
          <div className="flex gap-2">
            {shift && <Button type="button" variant="outline" onClick={remove} className="text-rose-600" data-testid="shift-delete-btn"><Trash2 className="h-4 w-4" /></Button>}
            <Button type="submit" className="flex-1 rounded-full" disabled={!f.date} data-testid="shift-save-btn">Save shift</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
